import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { Layout } from '../../components/Layout';
import { useAuth } from '../../contexts/AuthContext';

const MIN_COHORT_SIZE = 5;

// Statuses considered "interview or beyond" — current status field only.
// Limitation: a job rejected after an interview is not counted here (would require
// checking statusHistory arrays). For cohort-level reporting this is an acceptable proxy.
const INTERVIEW_OR_BEYOND = ['interview', 'offer', 'landed'];

type NoteCategory = 'mock_interview' | 'resume_review' | 'offer_negotiation' | 'job_search_strategy' | 'linkedin_branding' | 'general_checkin' | 'other';

const NOTE_CATEGORIES: NoteCategory[] = ['mock_interview', 'resume_review', 'offer_negotiation', 'job_search_strategy', 'linkedin_branding', 'general_checkin', 'other'];

const CATEGORY_LABELS: Record<NoteCategory, string> = {
  mock_interview: 'Mock Interview',
  resume_review: 'Resume Review',
  offer_negotiation: 'Offer Negotiation',
  job_search_strategy: 'Job Search Strategy',
  linkedin_branding: 'LinkedIn/Branding',
  general_checkin: 'General Check-in',
  other: 'Other',
};

function tsToMs(ts: any): number {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.seconds === 'number') return ts.seconds * 1000;
  if (typeof ts === 'string') { const n = new Date(ts).getTime(); return isNaN(n) ? 0 : n; }
  return 0;
}

function fmtDate(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function fmtShortDate(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

interface ReportStats {
  totalEnrolled: number;
  approved: number;
  pending: number;
  active: number;
  placed: number;
  paused: number;
  programStart: Date | null;
  activeInLast30d: number;
  totalApplications: number;
  interviewReached: number;
  coachingCounts: Record<string, number>;
}

export function ClientReport() {
  const { clientId } = useParams<{ clientId: string }>();
  const { firmAdmin } = useAuth();
  const { firmId, firm } = firmAdmin!;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tooSmall, setTooSmall] = useState(false);
  const [clientName, setClientName] = useState('');
  const [stats, setStats] = useState<ReportStats | null>(null);

  const reportDate = fmtDate(new Date());

  useEffect(() => { if (clientId) load(); }, [clientId]);

  const load = async () => {
    setLoading(true);
    setError('');
    setTooSmall(false);
    try {
      // 1. Verify the client belongs to this firm.
      const clientSnap = await getDoc(doc(db, 'clients', clientId!));
      if (!clientSnap.exists() || clientSnap.data().firmId !== firmId) {
        setError('Client not found.');
        return;
      }
      setClientName(clientSnap.data().name ?? '');

      // 2. Fetch all profiles for this client.
      // firmId filter is required: the firm-admin read rule on profiles checks
      // isFirmAdminLive(resource.data.firmId), so the query must constrain firmId
      // to give Firestore's rule evaluator something it can verify statically.
      // clientId does the actual scoping to this specific client.
      const profilesSnap = await getDocs(
        query(
          collection(db, 'profiles'),
          where('firmId', '==', firmId),
          where('clientId', '==', clientId),
        )
      );
      const profiles = profilesSnap.docs.map(d => {
        const p = d.data();
        return {
          uid: d.id,
          enrollmentStatus: p.enrollmentStatus ?? null,
          engagementStatus: p.engagementStatus ?? null,
          enrolledAt: p.enrolledAt,
          lastJobActivityAt: p.lastJobActivityAt,
        };
      });

      // Cohort guard — suppress report if too few candidates to aggregate safely.
      if (profiles.length < MIN_COHORT_SIZE) {
        setTooSmall(true);
        return;
      }

      // 3. Enrollment + engagement counts.
      const totalEnrolled = profiles.length;
      const approved = profiles.filter(p => p.enrollmentStatus === 'approved').length;
      const pending = profiles.filter(p => p.enrollmentStatus === 'pending').length;
      const active = profiles.filter(p => (p.engagementStatus ?? 'active') === 'active').length;
      const placed = profiles.filter(p => p.engagementStatus === 'placed').length;
      const paused = profiles.filter(p => p.engagementStatus === 'paused').length;

      // 4. Program start = earliest enrolledAt.
      const enrolledAtMs = profiles.map(p => tsToMs(p.enrolledAt)).filter(ms => ms > 0);
      const programStart = enrolledAtMs.length > 0 ? new Date(Math.min(...enrolledAtMs)) : null;

      // 5. Candidates with job activity in the last 30 days.
      const cutoff30 = Date.now() - 30 * 24 * 60 * 60 * 1000;
      const activeInLast30d = profiles.filter(p => tsToMs(p.lastJobActivityAt) > cutoff30).length;

      // 6. Job aggregates — requires a Firestore rule allowing firm admin to read jobs
      //    for their candidates (added in firestore.rules). Batched in chunks of 30 since
      //    Firestore's `in` operator supports max 30 values.
      const uids = profiles.map(p => p.uid);
      let totalApplications = 0;
      let interviewReached = 0;

      if (uids.length > 0) {
        const chunks: string[][] = [];
        for (let i = 0; i < uids.length; i += 30) chunks.push(uids.slice(i, i + 30));
        const jobSnaps = await Promise.all(
          chunks.map(chunk =>
            getDocs(query(collection(db, 'jobs'), where('userId', 'in', chunk)))
          )
        );
        for (const snap of jobSnaps) {
          for (const d of snap.docs) {
            const j = d.data();
            totalApplications++;
            if (INTERVIEW_OR_BEYOND.includes(j.status)) interviewReached++;
          }
        }
      }

      // 7. Shared coach notes by category — only shared notes (private never counted).
      let coachingCounts: Record<string, number> = {};
      if (uids.length > 0) {
        const noteSnaps = await Promise.all(
          uids.map(uid =>
            getDocs(query(
              collection(db, 'coachNotes', uid, 'entries'),
              where('visibility', '==', 'shared'),
            ))
          )
        );
        for (const snap of noteSnaps) {
          for (const d of snap.docs) {
            const cat: string = d.data().category ?? 'general_checkin';
            coachingCounts[cat] = (coachingCounts[cat] ?? 0) + 1;
          }
        }
      }

      setStats({ totalEnrolled, approved, pending, active, placed, paused, programStart, activeInLast30d, totalApplications, interviewReached, coachingCounts });
    } catch (e: any) {
      setError(e.message || 'Failed to generate report.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <style>{`
        @media print {
          .nav { display: none !important; }
          .no-print { display: none !important; }
          body { background: white !important; }
          .card { box-shadow: none !important; border: 1px solid #ddd !important; }
          .stat-card { box-shadow: none !important; border: 1px solid #ddd !important; }
        }
      `}</style>

      <div style={{ maxWidth: 800 }}>
        <div className="no-print" style={{ marginBottom: 20 }}>
          <Link to="/firm/clients" style={{ fontSize: 13, color: 'var(--text-secondary)', textDecoration: 'none' }}>
            ← Back to Clients
          </Link>
        </div>

        {error && <div className="error-message" style={{ marginBottom: 16 }}>{error}</div>}

        {loading ? (
          <div className="loading">Loading…</div>
        ) : tooSmall ? (
          <div style={{
            padding: '24px 28px',
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            fontSize: 14,
            color: 'var(--text-secondary)',
            lineHeight: 1.7,
          }}>
            <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>Report not yet available</div>
            This client has too few enrolled candidates to generate an aggregate report without risking individual identification. A report will be available once enrollment grows.
          </div>
        ) : stats ? (
          <>
            {/* Report header */}
            <div style={{ marginBottom: 32 }}>
              <h1 style={{ fontSize: 26, fontWeight: 700, margin: '0 0 6px' }}>{clientName}</h1>
              <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 4 }}>{firm.name}</div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                Report generated {reportDate}
                {stats.programStart && (
                  <> · Since program start: {fmtShortDate(stats.programStart)}</>
                )}
              </div>
            </div>

            {/* Enrollment */}
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>
              Enrollment
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 32 }}>
              <StatCard label="Total Enrolled" value={stats.totalEnrolled} />
              <StatCard label="Approved" value={stats.approved} />
              <StatCard label="Pending" value={stats.pending} />
            </div>

            {/* Engagement */}
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>
              Engagement
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 32 }}>
              <StatCard label="Active" value={stats.active} />
              <StatCard label="Placed" value={stats.placed} />
              <StatCard label="Paused" value={stats.paused} />
            </div>

            {/* Activity */}
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>
              Activity
            </div>
            <div className="card" style={{ marginBottom: 32 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
                <ActivityRow label="Total applications tracked" value={stats.totalApplications} border={false} />
                <ActivityRow label="Reached interview or offer" value={stats.interviewReached} border={false} />
                <ActivityRow label="Candidates active in last 30 days" value={stats.activeInLast30d} border={true} span={true} />
              </div>
            </div>

            {/* Coaching Activity */}
            {NOTE_CATEGORIES.some(cat => (stats.coachingCounts[cat] ?? 0) > 0) && (
              <>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>
                  Coaching Activity
                </div>
                <div className="card" style={{ marginBottom: 32 }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
                    {NOTE_CATEGORIES.filter(cat => (stats.coachingCounts[cat] ?? 0) > 0).map(cat => (
                      <div key={cat} style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                        <span style={{ fontSize: 22, fontWeight: 700 }}>{stats.coachingCounts[cat]}</span>
                        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{CATEGORY_LABELS[cat]}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Download */}
            <button
              className="btn-secondary no-print"
              style={{ padding: '8px 20px', fontSize: 13 }}
              onClick={() => window.print()}
            >
              Download PDF
            </button>
          </>
        ) : null}
      </div>
    </Layout>
  );
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="stat-card">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
    </div>
  );
}

function ActivityRow({ label, value, border, span }: { label: string; value: number; border: boolean; span?: boolean }) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      padding: '12px 0',
      borderTop: border ? '1px solid var(--border)' : 'none',
      gridColumn: span ? '1 / -1' : undefined,
    }}>
      <span style={{ fontSize: 14, color: 'var(--text-secondary)' }}>{label}</span>
      <span style={{ fontSize: 22, fontWeight: 700 }}>{value}</span>
    </div>
  );
}
